import React from 'react';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { ITicketProps } from '../../../../../../types/Ticket/ITicket';
import { TicketApi } from '../../../../../../redux/api/TicketApi';
import { useReduxSelector } from '../../../../../../redux/hooks';
import { SvgIcon } from "@progress/kendo-react-common";
import * as svg from '@progress/kendo-svg-icons';
import { Avatar, Card, CardBody, CardHeader, PanelBar, PanelBarItem } from '@progress/kendo-react-layout';
import FilterBanner from '../Filter/FilterBanner';
import { IAction } from '../../../../../../types/Action/IAction';
import './TicketStoryLine.scss';
import { UserApi } from '../../../../../../redux/api/UserApi';
import { Setting } from '../../../../../../types/Setting/Setting';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import TicketStoryLine3, { IChartData } from './TicketStoryLine3';
import { SystemApi } from '../../../../../../redux/api/SystemApi';
import TooManyRecordsBanner from './TooManyRecordsBanner';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { PartialSpinny } from '../../../../../Nav/Spinny';

const TicketStoryLine2: React.FC<ITicketProps> = (ticket) => {
    const { data: notesColorSetting } = UserApi.useGetSettingQuery(Setting.END_USER_NOTES_COLOR);
    const filter = useReduxSelector(state => state.filter);
    const { data: userProfile } = TicketApi.useGetUserProfileQuery();
    const startDate = new Date(ticket?.Open_Dt).toISOString().split('T')[0];
    const { data: calendar, isFetching: fetchingDays } = SystemApi.useGetWorkHoursQuery({ slaId: ticket?.SLA_Id, start: startDate, end: ((ticket.Open_Status === 0 && ticket.Close_Dt) || null) });
    const { data: actions, refetch, isFetching } = TicketApi.useGetActionsQuery({ ticketId: Number(ticket?.ID), take: 100, skip: 0, ...filter }, { skip: ticket?.ID === undefined });
    const { data: stateHistory, isFetching: fetchingHistory } = TicketApi.useGetStateHistoryQuery((ticket?.ID));
    const [view, setView] = React.useState({ text: 'Daily', value: 0 });

    const getKeyFromDate = (_date: Date, isZulu: boolean) => {
        var date = new Date(_date.toISOString().replace('Z', '') + (isZulu ? 'Z' : ''));
        console.log('getKeyFromDate', date.toLocaleString());

        //Hourly
        if (view?.value === 1) {
            date.setMinutes(0, 0, 0);
            return date.toLocaleDateString("en-US") + ' ' + date.toLocaleString([], { hour: '2-digit', minute: '2-digit' });
        }
        return date.toLocaleDateString("en-US");
    }

    const getColorFromState = (stata: string) => {
        let iconColor = '';
        if (stata) {
            if (stata === 'Closed' || stata.indexOf('Late') > -1) {
                iconColor = '#ff0000';//Closed (Red)
            } else if (stata.indexOf('Risk') > -1) {
                iconColor = '#ecbe4e'; //Yello
            } else if (stata === 'Current') {
                iconColor = '#697cc1';//Blue
            } else if (stata === 'New') {
                iconColor = '#73a913';//Green
            }
        } else {
            iconColor = '#73a913';//Green
        }
        return iconColor;
    }

    const groupedActionsByDay = actions?.Items.filter(a => a.ID).reduce((acc, action) => {
        //const date = new Date(action.Action_Dt + 'Z').toDateString();
        const date = getKeyFromDate(new Date(action.Action_Dt), true);
        if (!acc[date] || !acc[date].actions) {
            acc[date] = { actions: [] } as IChartData;
        }
        acc[date].actions.push({ ...action, Action_Dt: new Date(action.Action_Dt + 'Z') });
        return acc;
    }, {} as { [key: string]: IChartData });



    //Add ticket created and closed events
    if (groupedActionsByDay) {
        var key = getKeyFromDate(new Date(ticket.Open_Dt + 'Z'), false)
        //console.log('Created', new Date(ticket.Open_Dt),ticket.Open_Dt,key);
        var createdAction = [{ Action_Notes: 'Created', Action_Type_Name: 'Created', Action_Dt: new Date(ticket.Open_Dt + 'Z'), Is_System_Action: true } as IAction];
        (!groupedActionsByDay[key])
            ? groupedActionsByDay[key] = { actions: createdAction, showActions: false } as IChartData
            : groupedActionsByDay[key]?.actions.push(createdAction[0]);

        //if(ticket.Close_Dt) { groupedActionsByDay[ticket.Open_Dt] = [{ Action_Notes: 'Ticket Closed', Action_Dt: ticket.Close_Dt, Staff_Name: ticket.Closed_By, Is_System_Action: true }];}

        //Add State Changes
        if (stateHistory)
            stateHistory?.filter(s => s.Old_State || s.New_State !== 'New').map((state) => {
                var key = getKeyFromDate(new Date(state.Created_Dt), true);
                var newDayAction = [{ Action_Notes: 'State Changed to ' + state.New_State, Action_Dt: new Date(state.Created_Dt + 'Z'), Staff_Name: '', Is_System_Action: true }] as IAction[]
                if (!groupedActionsByDay[key])
                    groupedActionsByDay[key] = { actions: newDayAction, color: getColorFromState(state?.New_State), showActions: false } as IChartData
                else {
                    groupedActionsByDay[key].actions.push(newDayAction[0])
                    groupedActionsByDay[key].showActions = true
                }

            });
    }

    const formatAction = (action: IAction) => {
        var time = new Date(action.Action_Dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        //console.log('formatAction',new Date(action.Action_Dt),action.Action_Dt,time,action)
        return action.Is_System_Action
            ? <div className='p-2'><Avatar ><img src='images/Host_Images/System_Action/Gears.png'></img></Avatar>{time} {action.Staff_Name} - <div className='d-inline' dangerouslySetInnerHTML={{ __html: action.HTML_Notes || action.Action_Notes }}></div></div>
            : <div className='p-2'><Avatar ><img src={action.Profile_Image_Url} height={50} width={50}></img></Avatar> {time} - {action.Staff_Name}: <div className='d-inline' dangerouslySetInnerHTML={{ __html: action.HTML_Notes || action.Action_Notes }}></div></div>
    }




    //Add Closed
    var closed = new Date(ticket?.Close_Dt + 'Z');
    if (ticket && (closed instanceof Date && !isNaN(closed.valueOf())) && groupedActionsByDay) {
        const key = getKeyFromDate(new Date(ticket?.Close_Dt + 'Z'), false);
        //console.log('closed', closed, key);
        if (groupedActionsByDay && !groupedActionsByDay[key]) {
            groupedActionsByDay[key] = { showActions: false, color: getColorFromState(ticket?.NextSLAMilestoneName), actions: [{ Action_Dt: new Date(ticket?.Close_Dt), Action_Type_Name: 'Closed', Is_System_Action: true }] } as IChartData;
        } else {
            if (!groupedActionsByDay[key].actions) { groupedActionsByDay[key].actions = [] as IAction[]; }
            groupedActionsByDay[key].actions.push({ Action_Dt: new Date(ticket?.Close_Dt + 'Z'), Action_Type_Name: 'Closed', Action_Notes: 'Closed', Is_System_Action: true } as IAction);
        }
    } else {
        //Add NOW
        var now = new Date();//new Date().toISOString().replace('Z', ''));//new Date();
        //round to the nearest minute
        now.setSeconds(0, 0);
        const key = getKeyFromDate(now, false);

        if (groupedActionsByDay && !groupedActionsByDay[key]) {
            groupedActionsByDay[key] = { showActions: false, color: getColorFromState(ticket?.NextSLAMilestoneName), actions: [{ Action_Dt: now, Action_Type_Name: 'Now', Is_System_Action: true }] } as IChartData;
        }

        //console.log('NOW', now, groupedActionsByDay);
    }



    //Add calendar days in between all the action days so we can see how long ago the last action was
    if (calendar) {
        groupedActionsByDay && Object.keys(groupedActionsByDay).map((key) => {
            //Find the number of calendar days between the last action and this action
            var keyDate = new Date(key);

            var fullDays = calendar.filter(c => new Date(c.Begin_datetime) <= new Date(key))
            var partialHours = calendar.filter(c => new Date(c.End_datetime) > new Date(key) && new Date(c.End_datetime) > new Date(key))

            //console.log('daysOpen',fullDays,partialHours,keyDate);

            var daysOpen = view?.value === 0 ? fullDays.length : Math.round(fullDays.map(c => {
                //The day start date is either the ticket open date or the calendar start date
                var dayStart = new Date(Math.max(new Date(c.Begin_datetime + 'Z').getTime(), new Date(ticket.Open_Dt + 'Z').getTime()))
                console.warn('Begin_datetime', new Date(c.Begin_datetime + 'Z').toISOString(), 'dayStart', dayStart.toISOString(), (keyDate.getTime() - dayStart.getTime()) / 1000 / 60, 'keyDate', keyDate.toISOString())
                var dayEnd = new Date(c.End_datetime + 'Z')
                //console.warn('keyDate', dayEnd, keyDate, dayEnd > keyDate, (dayEnd.getTime() - keyDate.getTime()) / 1000 / 60);
                //Only add partial hours if we are say half way through the day
                if (dayEnd > keyDate)
                    return Math.max(0, (keyDate.getTime() - dayStart.getTime()) / 1000 / 60);
                else
                    return c.Work_mins
            }
            ).reduce((a, b) => a + b, 0) / 60);

            groupedActionsByDay[key].daysOpen = daysOpen;

        });
        // calendar.map((cal) => {
        //     var key = getKeyFromDate(cal.Day)
        //     if (!groupedActionsByDay[key]) {
        //         groupedActionsByDay[key] = { actions: [] } as IChartData;
        //     }
        // });
    }




    const storyData = groupedActionsByDay && Object.keys(groupedActionsByDay).sort((a, b) => new Date(b).getTime() - new Date(a).getTime()).map((day) => {
        var icon = <span><Tooltip anchorElement='pointer' position="top" content={() => day}><SvgIcon className='icon' icon={svg.stickyNoteIcon} /></Tooltip></span>
        const actionCount = groupedActionsByDay[day].actions?.length;
        const isSystem = actionCount === 1 && groupedActionsByDay[day].actions[0].Is_System_Action;
        const isSingleEndUserAction = actionCount === 1 && groupedActionsByDay[day].actions[0].Is_Client_Role;
        var headerText = actionCount > 0 ? (actionCount > 1 ? `+${actionCount} action` : `+${actionCount} action`) : groupedActionsByDay[day].label;
        if (isSingleEndUserAction) { headerText = `Client action` }
        var header = <div style={{ background: isSingleEndUserAction && notesColorSetting?.Bool_Value && notesColorSetting?.String_Value ? notesColorSetting?.String_Value : '' }}>{headerText}</div>;
        var showActions = groupedActionsByDay[day].showActions !== false; //undefined or true
        if (isSystem) {
            var actionDate = new Date(groupedActionsByDay[day].actions[0].Action_Dt);// + 'Z');
            header = <>{actionDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {groupedActionsByDay[day].actions[0].Action_Type_Name || groupedActionsByDay[day].actions[0].Action_Notes}</>;
            icon = <SvgIcon className='icon' icon={svg.gearIcon} xlinkTitle='System action' />;
        }
        let iconColor = groupedActionsByDay[day].color;//New (Green)
        let dayOrMinsLabel = view?.value === 0 ? 'day' : 'hour';

        //Find what the last state was on this day
        if (stateHistory && !iconColor) {
            var thisDay = new Date(day);
            var state = stateHistory.slice().sort((a, b) => new Date(b?.Created_Dt).getTime() - new Date(a?.Created_Dt).getTime())
            var lastState = state.find(s => new Date(s.Created_Dt + 'Z') <= thisDay);
            iconColor = getColorFromState(lastState?.New_State);

        }

        //console.log('groupedActionsByDay',groupedActionsByDay)

        return (
            <>
                <VerticalTimelineElement
                    className="vertical-timeline-element--work"
                    contentStyle={{}}
                    contentArrowStyle={{}}
                    date={groupedActionsByDay[day].daysOpen + (groupedActionsByDay[day].daysOpen === 1 ? ` ${dayOrMinsLabel} open` : ` ${dayOrMinsLabel}s open`)}
                    iconStyle={{ background: iconColor, color: '#fff' }}
                    icon={icon} >
                    {!showActions && header && <div className='header' title={day}>{header}</div>}
                    {showActions && <PanelBar>
                        <PanelBarItem id="actions" title={header}>
                            {groupedActionsByDay[day].actions.slice().sort((a, b) => new Date(b.Action_Dt).getTime() - new Date(a.Action_Dt).getTime()).map(action => {
                                return formatAction(action);
                            })}
                        </PanelBarItem>
                    </PanelBar>}
                </VerticalTimelineElement >
            </>)
    });

    if (fetchingDays || isFetching || fetchingHistory || ticket?.Is_Loading) return (<PartialSpinny />)

    return (
        <div className='storyline'>
            <Card>
                <CardHeader className='action-header flex-wrap' style={{ color: '#fff', background: userProfile?.Nav_Hex_Color }}>
                    <div className='col-10'>
                        <SvgIcon icon={svg.bookIcon} size='large' /> Storyline
                    </div>
                    <div className='col-2 d-flex flex-grow align-items-end'>
                        {/* Daily or hourly view */}
                        <DropDownList size='medium'
                            data={[{ text: 'Daily', value: 0 }, { text: 'Hourly', value: 1 }]}
                            textField="text"
                            dataItemKey="value"
                            value={view}
                            onChange={(e) => {
                                setView(e.target.value);
                                console.log('onChange', e.target.value)
                            }} />
                    </div>
                </CardHeader>
                <CardBody>
                    <FilterBanner />
                    <TooManyRecordsBanner recordCount={actions?.RowCount} />
                    {view?.value === 2 ? <TicketStoryLine3 {...ticket} /> :
                        <VerticalTimeline animate={false} >
                            {storyData}
                        </VerticalTimeline>}
                </CardBody>
            </Card>


        </div>
    );
};

export default TicketStoryLine2;